import { Phone, Plus, Trash2 } from 'lucide-react';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import CustomSelect from '../../../../../../../components/forms/CustomSelect';
import CustomDatePicker from '../../../../../../../components/forms/CustomDatePicker';
import Clock from '../../../../../../../components/forms/Clock';
import vouchersData from '../../../../../../../data/vouchers/vouchersData.json';

const contactLabels = vouchersData.contactLabels || [];

const ContactDetailsSection = ({
  formData,
  setFormData,
  addContact
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm" id="contact-details-section">
      <div className="flex items-center justify-between gap-2.5 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner">
            <Phone size={14} className="sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 tracking-wider">Contact Details</h3>
            <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">Add essential contact information for drivers, hotels, or guides</p>
          </div>
        </div>
        <button
          onClick={addContact}
          className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm cursor-pointer shrink-0"
          type="button"
        >
          <Plus size={12} className="sm:w-3.5 sm:h-3.5" />
          <span>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Contact</span>
          </span>
        </button>
      </div>

      {(!formData.contact_details || formData.contact_details.length === 0) ? (
        <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <p className="text-xs text-slate-400 font-medium">No contact details added yet. Click &ldquo;Add Contact&rdquo; to add trip coordinators.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {formData.contact_details.map((contact, idx) => (
            <div
              key={contact.id || idx}
              id={contact.id || `contact-${idx}`}
              className="relative border border-slate-200 rounded-3xl p-5 sm:p-6 bg-white hover:bg-slate-50/20 transition-all duration-300 shadow-sm hover:shadow-md hover:border-slate-300 group"
            >
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-100">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-black tracking-wider uppercase">
                  Contact #{idx + 1}
                </span>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, contact_details: prev.contact_details.filter((_, i) => i !== idx) }))}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all cursor-pointer"
                  title="Remove Contact Entry"
                  type="button"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="space-y-2">
                    <CustomSelect
                      label="Label"
                      value={['Driver', 'Helper', 'Guide', 'Tour Manager'].includes(contact.label) ? contact.label : (contact.label ? 'Custom' : '')}
                      onChange={(val) => {
                        const newC = [...(formData.contact_details || [])];
                        if (val === 'Custom' || val === 'Other') {
                          newC[idx].label = 'Custom';
                          newC[idx].vehicle_type = '';
                          newC[idx].vehicle_number = '';
                        } else {
                          newC[idx].label = val;
                          newC[idx].vehicle_type = '';
                          newC[idx].vehicle_number = '';
                        }
                        setFormData(prev => ({ ...prev, contact_details: newC }));
                      }}
                      options={contactLabels}
                      placeholder="Select Label..."
                      buttonClassName="h-[42px] px-3.5 text-xs font-semibold"
                    />
                    {(!['Driver', 'Helper', 'Guide', 'Tour Manager'].includes(contact.label) && contact.label !== '') && (
                      <CustomInput
                        value={contact.label === 'Other' || contact.label === 'Custom' ? '' : contact.label}
                        onChange={(e) => {
                          const newC = [...(formData.contact_details || [])];
                          newC[idx].label = e.target.value;
                          setFormData(prev => ({ ...prev, contact_details: newC }));
                        }}
                        placeholder="Enter Custom Label"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <CustomInput
                    label="Name"
                    value={contact.name || ''}
                    onChange={(e) => {
                      const newC = [...(formData.contact_details || [])];
                      newC[idx].name = e.target.value;
                      setFormData(prev => ({ ...prev, contact_details: newC }));
                    }}
                    placeholder="e.g. Neha Kapoor"
                  />
                </div>

                <div>
                  <CustomInput
                    label="Phone number"
                    icon={Phone}
                    mono
                    value={contact.number || ''}
                    onChange={(e) => {
                      const newC = [...(formData.contact_details || [])];
                      newC[idx].number = e.target.value;
                      setFormData(prev => ({ ...prev, contact_details: newC }));
                    }}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                {contact.label === 'Driver' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 border border-slate-200/80 rounded-2xl p-4 bg-slate-50/50 mt-2 col-span-1 md:col-span-3">
                    <div>
                      <CustomInput
                        label="Vehicle type / model"
                        value={contact.vehicle_type || ''}
                        onChange={(e) => {
                          const newC = [...(formData.contact_details || [])];
                          newC[idx].vehicle_type = e.target.value;
                          setFormData(prev => ({ ...prev, contact_details: newC }));
                        }}
                        placeholder="e.g. SUV, Innova"
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="Vehicle number"
                        mono
                        value={contact.vehicle_number || ''}
                        onChange={(e) => {
                          const newC = [...(formData.contact_details || [])];
                          newC[idx].vehicle_number = e.target.value;
                          setFormData(prev => ({ ...prev, contact_details: newC }));
                        }}
                        placeholder="e.g. GA-01-A-1234"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-bold text-slate-700 tracking-wide block">Assign Date</label>
                        <label className="flex items-center gap-1 text-[11px] font-bold text-primary cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={contact.date === undefined || contact.date === 'All Days'}
                            onChange={(e) => {
                              const newC = [...(formData.contact_details || [])];
                              if (e.target.checked) {
                                newC[idx].date = 'All Days';
                              } else {
                                newC[idx].date = '';
                              }
                              setFormData(prev => ({ ...prev, contact_details: newC }));
                            }}
                            className="rounded border-slate-200 text-primary focus:ring-primary h-3.5 w-3.5"
                          />
                          All Days
                        </label>
                      </div>
                      {(contact.date === undefined || contact.date === 'All Days') ? (
                        <div className="w-full h-[42px] px-4 flex items-center bg-slate-100 border border-slate-200 rounded-xl text-slate-500 text-xs sm:text-sm font-semibold">
                          All Days
                        </div>
                      ) : (
                        <CustomDatePicker
                          value={contact.date}
                          onChange={(val) => {
                            const newC = [...(formData.contact_details || [])];
                            newC[idx].date = val;
                            setFormData(prev => ({ ...prev, contact_details: newC }));
                          }}
                          placeholder="Select Date"
                          inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none shadow-sm"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Time From</label>
                      <Clock
                        value={contact.time_from || ''}
                        onChange={(val) => {
                          const newC = [...(formData.contact_details || [])];
                          newC[idx].time_from = val;
                          setFormData(prev => ({ ...prev, contact_details: newC }));
                        }}
                        placeholder="00:00"
                        inputClassName="h-[42px] px-3 py-2 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Time To</label>
                      <Clock
                        value={contact.time_to || ''}
                        onChange={(val) => {
                          const newC = [...(formData.contact_details || [])];
                          newC[idx].time_to = val;
                          setFormData(prev => ({ ...prev, contact_details: newC }));
                        }}
                        placeholder="00:00"
                        inputClassName="h-[42px] px-3 py-2 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactDetailsSection;
